/**
 * Phase 15: 우선순위 선택 다이얼로그 (공통 컴포넌트)
 *
 * AppointmentsPage와 ConsultDetailPage에서 재사용
 * 1순위와 2순위 예약 시간이 모두 있을 때 사용자가 선택할 수 있도록 함
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface AppointmentData {
  id: number;
  customerName: string;
  department: string;
  firstChoiceDate?: string | null;
  firstChoiceTime?: string | null;
  secondChoiceDate?: string | null;
  secondChoiceTime?: string | null;
}

export interface PrioritySelectionDialogProps {
  open: boolean;
  appointment: AppointmentData | null;
  onConfirm: (priority: 1 | 2) => void;
  onCancel: () => void;
}

export function PrioritySelectionDialog({
  open,
  appointment,
  onConfirm,
  onCancel,
}: PrioritySelectionDialogProps) {
  if (!appointment) return null;

  const hasFirst = appointment.firstChoiceDate && appointment.firstChoiceTime;
  const hasSecond = appointment.secondChoiceDate && appointment.secondChoiceTime;

  // 둘 다 없으면 다이얼로그 표시 안 함
  if (!hasFirst && !hasSecond) {
    return null;
  }

  // 하나만 있으면 자동 선택 (다이얼로그 불필요)
  if (hasFirst && !hasSecond) {
    // 1순위만 있음 - 자동 선택되므로 다이얼로그 표시 안 함
    return null;
  }

  if (!hasFirst && hasSecond) {
    // 2순위만 있음 - 자동 선택되므로 다이얼로그 표시 안 함
    return null;
  }

  // 둘 다 있는 경우에만 다이얼로그 표시
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-info text-white">
              우선순위 선택
            </Badge>
            예약 확정
          </DialogTitle>
          <DialogDescription>
            1순위와 2순위 예약 시간이 모두 있습니다. 어느 시간으로 확정하시겠습니까?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* 고객 정보 */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">고객명:</span>
              <span className="font-medium">{appointment.customerName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">진료과:</span>
              <span className="font-medium">{appointment.department}</span>
            </div>
          </div>

          {/* 1순위 예약 정보 */}
          {hasFirst && (
            <button
              onClick={() => onConfirm(1)}
              className="w-full bg-info/10 p-4 rounded-lg border-2 border-info/30 hover:border-info/60 transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="default" className="bg-info text-xs">
                  1순위 (고객 선호)
                </Badge>
              </div>
              <div className="text-sm font-medium">
                📅 {appointment.firstChoiceDate} {appointment.firstChoiceTime}
              </div>
            </button>
          )}

          {/* 2순위 예약 정보 */}
          {hasSecond && (
            <button
              onClick={() => onConfirm(2)}
              className="w-full bg-warning/10 p-4 rounded-lg border-2 border-warning/30 hover:border-warning/60 transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="bg-warning text-white text-xs">
                  2순위 (대안)
                </Badge>
              </div>
              <div className="text-sm font-medium">
                📅 {appointment.secondChoiceDate} {appointment.secondChoiceTime}
              </div>
            </button>
          )}

          <div className="text-xs text-muted-foreground bg-warning/10 p-3 rounded-lg border border-warning/30">
            ⚠️ 2순위로 확정 시 고객에게 변경 사항을 안내해주세요.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            취소
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
